import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReportProgressModalProps } from './types';
import styles from './ReportProgressModal.module.scss';

const ReportProgressModal: React.FC<ReportProgressModalProps> = ({
  open,
  total,
  processed,
  success,
  failed,
}) => {
  const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Generating Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Please wait while your reports are being prepared
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box className={styles.container}>

          {/* Circular progress with percentage label */}
          <Box className={styles.progressCircleWrapper}>
            {/* Background track */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={120}
              thickness={4}
              sx={{ color: 'action.disabledBackground' }}
            />
            {/* Foreground value */}
            <CircularProgress
              variant="determinate"
              value={percentage}
              size={120}
              thickness={4}
              sx={{ color: 'primary.main', position: 'absolute', top: 0, left: 0 }}
            />
            <Box className={styles.progressLabel}>
              <Typography variant="h5" fontWeight={700} lineHeight={1}>
                {percentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                complete
              </Typography>
            </Box>
          </Box>

          {/* processed / total label */}
          <Typography variant="body1" color="text.secondary">
            <strong>{processed}</strong> of <strong>{total}</strong> portfolios generated
          </Typography>

          {/* Linear progress bar */}
          <Box className={styles.linearProgressWrapper}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Success / Failed stats */}
          <Box className={styles.statsRow}>
            <Box className={styles.statBox}>
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label={`${success} succeeded`}
                color="success"
                variant="outlined"
                size="small"
              />
            </Box>
            {failed > 0 && (
              <Box className={styles.statBox}>
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label={`${failed} failed`}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>

        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportProgressModal;
